// @ts-strict-ignore
import {AttrsSchema} from 'panel';
import React from 'react';
import {Component} from 'panel';

import {SectionItem} from 'common/components/items-menu/types';
import {Placement} from 'common/components/tooltip/types';
import {formatText} from 'common/util/format-text';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {RevealableTextEditor} from 'common/components/revealable-text-editor';
import {IronContextRegistry} from 'common/report/context-registry';

import css from './index.inline.styl';
// eslint-disable-next-line import/no-cycle
import {template} from './template';
import {ContextPanelData} from './types';
import {openImageModal} from './image-modal/openImageModal';

export type ContextPanelAttrs = {
  'project-is-free-plan'?: boolean;
};

export type ContextPanelParams = {
  data?: ContextPanelData;
};

export type ContextPanelEventHandlers = {
  contextPanelHoverIn?: (ev: CustomEvent<EmptyObj>) => void;
  contextPanelHoverOut?: (ev: CustomEvent<EmptyObj>) => void;
  tooltipContextAction?: (ev: CustomEvent<any>) => void;
  tooltipContextEditDescription?: (ev: EditDescriptionCustomEvent) => void;
};

export type EditDescriptionCustomEvent = CustomEvent<{
  description: string;
  item: ContextPanelData[`editItem`];
}>;

type ContextPanelProps = {
  params?: ContextPanelParams;
  attrs?: ContextPanelAttrs;
  on?: ContextPanelEventHandlers;
};

type ContextPanelState = {
  description: string;
  expanded: boolean;
  openMoreDropdown: boolean;
};

export class ContextPanel extends Component<
  ContextPanelState,
  ContextPanelAttrs,
  EmptyObj,
  EmptyObj,
  IronContextRegistry,
  ContextPanelParams
> {
  private declare textEditor: RevealableTextEditor;

  static get attrsSchema(): AttrsSchema<ContextPanelAttrs> {
    return {
      ...super.attrsSchema,
      'project-is-free-plan': `boolean`,
    };
  }

  static get paramsSchema() {
    return {
      data: Object,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`urlManager` as const, `i18n` as const],
      defaultState: {
        description: ``,
        expanded: false,
        openMoreDropdown: false,
      },
      params: ContextPanel.paramsSchema,
      helpers: {
        formatText,

        handleTextEditorSave: (ev, item: ContextPanelData[`editItem`]) => {
          const description = ev.detail.value as string;
          this.dispatchEvent(
            new CustomEvent(`tooltipContextEditDescription`, {
              composed: true,
              cancelable: true,
              detail: {description, item},
            }) satisfies EditDescriptionCustomEvent,
          );
          this.update({description});
        },

        handleExpand: () => {
          this.update({expanded: true});
        },

        getActionItems: () => {
          const {actions} = this.params.data;
          const actionItems: SectionItem[] = [];

          actions?.forEach((action) => {
            actionItems.push({
              label: action.label,
              value: action.name,
              icon: action.icon,
              item: action.item,
              isDisabled: action.disabled,
              ...(action.disabled && action.disabledTooltipText
                ? {
                    tooltip: {
                      text: action.disabledTooltipText,
                      placement: Placement.Left,
                    },
                  }
                : {}),
            });
          });

          return actionItems;
        },

        handleMouseEnter: () => {
          this.dispatchEvent(new CustomEvent(`contextPanelHoverIn`));
        },

        handleActionClick: (actionItem) => {
          this.dispatchEvent(
            new CustomEvent(`tooltipContextAction`, {
              composed: true,
              cancelable: true,
              detail: {
                name: actionItem.value,
                item: actionItem.item,
              },
            }),
          );
        },

        handleMouseLeave: () => {
          this.dispatchEvent(new CustomEvent(`contextPanelHoverOut`));
        },

        editorInserted: (vnode) => {
          this.textEditor = vnode.elm;
        },

        actionClick: (action) => {
          this.dispatchEvent(
            new CustomEvent(`tooltipContextAction`, {
              composed: true,
              cancelable: true,
              detail: {
                name: action.name,
                item: action.item,
              },
            }),
          );
        },

        linkClick: (link) => window.open(link.href, `_blank`),

        showImageModalPromise: async (imageUrl: string) => {
          await openImageModal({imageUrl});
        },
      },
      hooks: {
        postUpdate: (_state, paramsUpdate?: ContextPanelParams) => {
          if (paramsUpdate?.data) {
            //TODO [shayan] find a better way
            if (this.textEditor) {
              this.textEditor.cancelEdit();
            }
            this.setDescription();
            this.closeMoreDropdown();
          }
        },
      },
    };
  }

  private setDescription() {
    if (this.params.data) {
      this.update({description: this.getContext(`i18n`).print(this.params.data.description)});
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.setDescription();
    this.closeMoreDropdown();
  }

  openMoreDropdown() {
    this.update({openMoreDropdown: true});
  }

  closeMoreDropdown() {
    this.update({openMoreDropdown: false});
  }

  getUrlManager() {
    return this.getContext(`urlManager`);
  }
}

export const ContextPanelEl = defineAndMakeJsxFactory<ContextPanelProps>(`mp-context-panel`, ContextPanel);
export const ContextPanelReact = panel2react<ContextPanelProps>(`mp-context-panel`, ContextPanel);

export type ContextPanelReactProps = React.ComponentProps<typeof ContextPanelReact>;
